/**
 * stepLabels — user-facing copy for in-progress pipeline steps.
 *
 * The backend's `RenterFileListItem.current_step` is the internal step
 * `name` (e.g. `'redaction_check'`). We translate it to a verb-phrase
 * that's meaningful to a renter watching their upload process.
 *
 * Why a frontend constant: copy is UX language, gets tweaked frequently,
 * and we don't want to redeploy the backend to update wording. If a new
 * backend step is added without a frontend update, we fall back to a
 * generic "Working".
 *
 * Source of step names: `applications/renter_hub/pipeline/steps/*.py`
 * (each step's `name = '...'` attribute).
 */
import type { RenterFileListItem } from 'src/types/aiFileUploadV2';

export const STEP_IN_PROGRESS_COPY: Record<string, string> = {
  duplicate_detection: 'Checking for duplicates',
  resolution_check: 'Checking image quality',
  corruption_check: 'Verifying file',
  image_extraction: 'Processing document',
  pdf_flatten: 'Preparing document',
  orientation_detection: 'Checking orientation',
  readability_check: 'Checking readability',
  foreign_object_detection: 'Scanning content',
  redaction_check: 'Reviewing for redactions',
  authenticity_check: 'Validating document',
  date_validation: 'Verifying dates',
  ocr_extraction: 'Reading text',
  classification: 'Identifying document type',
  extraction: 'Extracting your details',
};

export const PROCESSING_STEP_ORDER: readonly string[] = Object.keys(
  STEP_IN_PROGRESS_COPY
);

const FALLBACK_LABEL = 'Working';

/**
 * Translate a pipeline step name to user-facing copy. Full text is
 * preserved (no truncation / ellipsis) so callers can decide their own
 * presentation.
 *
 * @param stepName - The backend step name (e.g. `'redaction_check'`),
 *   typically `RenterFileListItem.current_step`. May be `null`/`undefined`
 *   when no step is currently running.
 * @returns A short verb-phrase to show to the user. Returns
 *   `'Working'` for unknown or missing step names.
 */
export function getStepLabel(stepName: string | null | undefined): string {
  if (!stepName) {
    return FALLBACK_LABEL;
  }
  return STEP_IN_PROGRESS_COPY[stepName] ?? FALLBACK_LABEL;
}

/**
 * Build the full processing-progress string the user sees on a row,
 * combining the verb label and the step counter. The counter is kept
 * (per UX request) so users can tell whether processing is moving.
 *
 * Examples:
 *   { current_step: 'extraction', completed_steps: 12, total_steps: 14 }
 *     -> 'Extracting your details (12/14)'
 *   { current_step: null, total_steps: 0 }
 *     -> 'Working'
 *
 * @param file Object with the three relevant fields. Accepts a wider
 *   shape than `RenterFileListItem` so tests / partial fixtures work.
 */
export function getStepProgressLabel(
  file: Pick<
    RenterFileListItem,
    'current_step' | 'completed_steps' | 'total_steps'
  >
): string {
  const label = getStepLabel(file.current_step);
  const total = file.total_steps ?? 0;
  if (total <= 0) {
    return label;
  }
  const done = file.completed_steps ?? 0;
  return `${label} (${done}/${total})`;
}

// Exported for tests so changes to the map are observable.
export const __TEST_ONLY__STEP_IN_PROGRESS_COPY = STEP_IN_PROGRESS_COPY;
